import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ScanLine, Plus, Loader2, FileText, Download, Trash2, ChevronLeft, Upload, CheckCircle2, AlertTriangle, FileSpreadsheet, Play, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const CHUNK_SIZE = 4 * 1024 * 1024;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const STATUS_BADGE = {
  draft: 'bg-gray-100 text-gray-700',
  ready: 'bg-blue-100 text-blue-700',
  processing: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

export default function ExamMarkingPage() {
  const [jobs, setJobs] = useState([]);
  const [view, setView] = useState('list'); // list | new | detail
  const [activeJob, setActiveJob] = useState(null);
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadJobs = useCallback(async () => {
    try {
      const res = await fetch('/api/exam-marking/jobs', { headers: authHeaders() });
      if (res.status === 403) { setJobs(null); setLoading(false); return; }
      const data = await res.json();
      setJobs(data.jobs || []);
    } catch { toast.error('Failed to load marking jobs'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  if (loading) return <div className="flex justify-center p-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  const duplicateJob = async (id) => {
    const res = await fetch(`/api/exam-marking/jobs/${id}/duplicate`, { method: 'POST', headers: authHeaders() });
    const d = await res.json();
    if (!res.ok) { toast.error(d.detail || 'Could not copy job'); return; }
    setResume(d);
    setView('new');
    toast.success(`Ready — same exam & answer key. Just upload the next ZIP!`);
  };

  if (jobs === null) return (
    <div className="p-8 text-center text-gray-600" data-testid="exam-marking-forbidden">
      <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
      AI Exam Marking is not enabled for your account yet.
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="exam-marking-page">
      {view === 'list' && (
        <JobList jobs={jobs} onNew={() => { setResume(null); setView('new'); }} onOpen={(j) => { setActiveJob(j); setView('detail'); }} onRefresh={loadJobs} onDuplicate={duplicateJob} />
      )}
      {view === 'new' && (
        <NewJobWizard resume={resume} onDone={(job) => { setResume(null); loadJobs(); setActiveJob(job); setView(job ? 'detail' : 'list'); }} onBack={() => { setResume(null); setView('list'); }} />
      )}
      {view === 'detail' && activeJob && (
        <JobDetail jobId={activeJob.id} onBack={() => { loadJobs(); setView('list'); }} onDuplicate={duplicateJob} />
      )}
    </div>
  );
}

const JobList = ({ jobs, onNew, onOpen, onRefresh, onDuplicate }) => {
  const deleteJob = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this marking job and all its results?')) return;
    await fetch(`/api/exam-marking/jobs/${id}`, { method: 'DELETE', headers: authHeaders() });
    toast.success('Job deleted');
    onRefresh();
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ScanLine className="w-7 h-7 text-blue-600" /> AI Exam Marking</h1>
          <p className="text-gray-500 text-sm mt-1">Upload an answer key + student exam photos — AI marks every paper with feedback</p>
        </div>
        <Button onClick={onNew} data-testid="new-marking-job-btn"><Plus className="w-4 h-4 mr-1" /> New Marking Job</Button>
      </div>
      {jobs.length === 0 ? (
        <div className="border-2 border-dashed rounded-xl p-16 text-center text-gray-400" data-testid="no-jobs-placeholder">
          <ScanLine className="w-12 h-12 mx-auto mb-3" />
          No marking jobs yet. Click "New Marking Job" to mark your first exam set.
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map((j) => (
            <div key={j.id} onClick={() => onOpen(j)} data-testid={`job-card-${j.id}`}
              className="border rounded-xl p-4 flex items-center justify-between cursor-pointer hover:shadow-md transition-shadow bg-white">
              <div>
                <div className="font-semibold">{j.title}</div>
                <div className="text-sm text-gray-500">{j.subject} {j.year_level && `• Year ${j.year_level}`} • {j.student_count || 0} students • {new Date(j.created_at).toLocaleDateString()}</div>
                {j.status === 'processing' && j.total_pages > 0 && (
                  <div className="mt-2 w-64 bg-gray-200 rounded-full h-2">
                    <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${Math.round((j.progress / j.total_pages) * 100)}%` }} />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_BADGE[j.status] || ''}`}>{j.status}</span>
                <button onClick={(e) => { e.stopPropagation(); onDuplicate(j.id); }} title="New group with same exam & answer key" className="text-gray-400 hover:text-blue-600" data-testid={`duplicate-job-${j.id}`}><Copy className="w-4 h-4" /></button>
                <button onClick={(e) => deleteJob(e, j.id)} className="text-gray-400 hover:text-red-500" data-testid={`delete-job-${j.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const NewJobWizard = ({ onDone, onBack, resume }) => {
  const [step, setStep] = useState(resume ? (resume.has_answer_key ? 3 : 2) : 1);
  const [title, setTitle] = useState(resume?.title || '');
  const [subject, setSubject] = useState('ICT');
  const [yearLevel, setYearLevel] = useState('12');
  const [jobId, setJobId] = useState(resume?.job_id || null);
  const [keyInfo, setKeyInfo] = useState(null);
  const [zipInfo, setZipInfo] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [busy, setBusy] = useState(false);
  const keyRef = useRef();
  const zipRef = useRef();

  const createJob = async () => {
    if (!title.trim()) { toast.error('Enter an exam title'); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/exam-marking/jobs', {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, subject, year_level: yearLevel }),
      });
      const data = await res.json();
      setJobId(data.job_id);
      setStep(2);
    } catch { toast.error('Failed to create job'); }
    setBusy(false);
  };

  const uploadKey = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`/api/exam-marking/jobs/${jobId}/answer-key`, { method: 'POST', headers: authHeaders(), body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setKeyInfo(data);
      toast.success('Answer key uploaded');
      setStep(3);
    } catch (e) { toast.error(e.message || 'Answer key upload failed'); }
    setBusy(false);
  };

  const uploadZip = async (file) => {
    if (!file) return;
    setBusy(true);
    setUploadPct(0);
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    try {
      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append('chunk_index', i);
        fd.append('total_chunks', totalChunks);
        fd.append('file', chunk, 'chunk.bin');
        const res = await fetch(`/api/exam-marking/jobs/${jobId}/upload-chunk`, { method: 'POST', headers: authHeaders(), body: fd });
        if (!res.ok) throw new Error('Chunk upload failed');
        setUploadPct(Math.round(((i + 1) / totalChunks) * 100));
      }
      const cfd = new FormData();
      cfd.append('total_size', file.size);
      cfd.append('total_chunks', totalChunks);
      const res = await fetch(`/api/exam-marking/jobs/${jobId}/upload-complete`, { method: 'POST', headers: authHeaders(), body: cfd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setZipInfo(data);
      toast.success(`Found ${Object.keys(data.students).length} students, ${data.total_pages} pages`);
    } catch (e) { toast.error(e.message || 'ZIP upload failed'); }
    setBusy(false);
  };

  const startMarking = async () => {
    setBusy(true);
    try {
      const res = await fetch(`/api/exam-marking/jobs/${jobId}/start`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success('AI marking started!');
      onDone({ id: jobId });
    } catch (e) { toast.error(e.message || 'Failed to start'); setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={onBack} className="flex items-center text-sm text-gray-500 mb-4 hover:text-gray-800" data-testid="wizard-back-btn"><ChevronLeft className="w-4 h-4" /> Back</button>
      <h2 className="text-xl font-bold mb-1">New Marking Job</h2>
      {resume && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 mt-2" data-testid="resume-banner">
          Reusing exam <b>"{resume.title}"</b> — {resume.has_answer_key ? 'answer key already loaded. Just upload the next group ZIP!' : 'upload the answer key.'}
        </div>
      )}
      <div className="flex gap-2 my-4">
        {[1, 2, 3].map((s) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full ${step >= s ? 'bg-blue-600' : 'bg-gray-200'}`} />
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4 bg-white border rounded-xl p-6" data-testid="wizard-step-1">
          <div>
            <label className="text-sm font-medium">Exam Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Year 12 ICT Mid-Year Exam 2026" data-testid="exam-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Subject</label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="exam-subject-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Year Level</label>
              <Input value={yearLevel} onChange={(e) => setYearLevel(e.target.value)} data-testid="exam-year-input" />
            </div>
          </div>
          <Button onClick={createJob} disabled={busy} className="w-full" data-testid="create-job-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue'}
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="bg-white border rounded-xl p-6 text-center" data-testid="wizard-step-2">
          <FileText className="w-10 h-10 mx-auto text-blue-600 mb-3" />
          <h3 className="font-semibold mb-1">Upload Answer Key / Marking Scheme</h3>
          <p className="text-sm text-gray-500 mb-4">PDF, Word or text file with answers and mark allocations</p>
          <input ref={keyRef} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={(e) => uploadKey(e.target.files[0])} data-testid="answer-key-file-input" />
          <Button onClick={() => keyRef.current.click()} disabled={busy} data-testid="upload-key-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />} Choose Answer Key
          </Button>
        </div>
      )}

      {step === 3 && (
        <div className="bg-white border rounded-xl p-6" data-testid="wizard-step-3">
          <div className="text-center">
            <Upload className="w-10 h-10 mx-auto text-blue-600 mb-3" />
            <h3 className="font-semibold mb-1">Upload Student Papers (ZIP)</h3>
            <p className="text-sm text-gray-500 mb-4">One folder per student inside the ZIP, containing photos of their exam pages</p>
            <input ref={zipRef} type="file" accept=".zip" className="hidden" onChange={(e) => uploadZip(e.target.files[0])} data-testid="zip-file-input" />
            {!zipInfo && (
              <Button onClick={() => zipRef.current.click()} disabled={busy} data-testid="upload-zip-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />} Choose ZIP File
              </Button>
            )}
            {busy && uploadPct > 0 && (
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-blue-600 h-2.5 rounded-full transition-all" style={{ width: `${uploadPct}%` }} />
                </div>
                <div className="text-sm text-gray-500 mt-1" data-testid="upload-progress-text">Uploading… {uploadPct}%</div>
              </div>
            )}
          </div>
          {zipInfo && (
            <div className="mt-4">
              <div className="flex items-center gap-2 text-green-700 font-medium mb-2"><CheckCircle2 className="w-5 h-5" /> {Object.keys(zipInfo.students).length} students found ({zipInfo.total_pages} pages)</div>
              <div className="max-h-44 overflow-y-auto border rounded-lg p-3 text-sm space-y-1" data-testid="students-preview-list">
                {Object.entries(zipInfo.students).map(([name, pages]) => (
                  <div key={name} className="flex justify-between"><span>{name}</span><span className="text-gray-400">{pages} pages</span></div>
                ))}
              </div>
              <Button onClick={startMarking} disabled={busy} className="w-full mt-4 bg-green-600 hover:bg-green-700" data-testid="start-marking-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />} Start AI Marking
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const JobDetail = ({ jobId, onBack, onDuplicate }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/exam-marking/jobs/${jobId}`, { headers: authHeaders() });
      setData(await res.json());
    } catch {}
  }, [jobId]);

  useEffect(() => {
    load();
    const iv = setInterval(() => {
      setData((d) => {
        if (d?.job?.status === 'processing' || d?.job?.status === 'ready' || !d) load();
        return d;
      });
    }, 4000);
    return () => clearInterval(iv);
  }, [load]);

  if (!data) return <div className="flex justify-center p-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  const { job, results } = data;
  const pct = job.total_pages ? Math.round((job.progress / job.total_pages) * 100) : 0;

  const downloadPdf = async (r) => {
    const res = await fetch(`/api/exam-marking/jobs/${jobId}/results/${r.id}/pdf`, { headers: authHeaders() });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${r.student_name} - MARKED.pdf`;
    a.click();
  };

  const downloadExcel = async () => {
    const res = await fetch(`/api/exam-marking/jobs/${jobId}/excel`, { headers: authHeaders() });
    if (!res.ok) { toast.error('No results yet'); return; }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${job.title} - Results.xlsx`;
    a.click();
  };

  return (
    <div data-testid="job-detail-view">
      {publishOpen && <PublishModal jobId={jobId} jobTitle={job.title} onClose={() => setPublishOpen(false)} />}
      <button onClick={onBack} className="flex items-center text-sm text-gray-500 mb-4 hover:text-gray-800" data-testid="detail-back-btn"><ChevronLeft className="w-4 h-4" /> All Jobs</button>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">{job.title}</h2>
          <div className="text-sm text-gray-500">{job.subject} {job.year_level && `• Year ${job.year_level}`} • {job.student_count} students</div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_BADGE[job.status] || ''}`} data-testid="job-status-badge">{job.status}</span>
          <Button variant="outline" onClick={() => onDuplicate(jobId)} data-testid="detail-duplicate-btn"><Copy className="w-4 h-4 mr-1" /> New Group (same exam)</Button>
          {results.length > 0 && (
            <>
              <Button variant="outline" onClick={downloadExcel} data-testid="download-excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
              <Button onClick={() => setPublishOpen(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="publish-gradebook-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Publish to Gradebook
              </Button>
            </>
          )}
        </div>
      </div>

      {job.status === 'processing' && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="processing-banner">
          <div className="flex items-center gap-2 text-amber-800 font-medium mb-2"><Loader2 className="w-4 h-4 animate-spin" /> AI is marking… {job.progress}/{job.total_pages} pages ({pct}%)</div>
          <div className="w-full bg-amber-100 rounded-full h-2.5">
            <div className="bg-amber-500 h-2.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-xs text-amber-700 mt-2">You can leave this page — marking continues in the background.</div>
        </div>
      )}
      {job.status === 'failed' && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4" data-testid="failed-banner">
          <div className="text-red-700 text-sm mb-3">Marking failed: {job.error}</div>
          <Button size="sm" onClick={async () => {
            const res = await fetch(`/api/exam-marking/jobs/${jobId}/start`, { method: 'POST', headers: authHeaders() });
            const d = await res.json();
            if (res.ok) { toast.success('Marking restarted'); load(); }
            else toast.error(d.detail || 'Could not restart');
          }} className="bg-red-600 hover:bg-red-700" data-testid="retry-marking-btn">
            <Play className="w-3.5 h-3.5 mr-1" /> Retry Marking
          </Button>
        </div>
      )}

      {results.length > 0 && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="p-3">#</th><th className="p-3">Student</th><th className="p-3">Score</th><th className="p-3">%</th><th className="p-3">Flags</th><th className="p-3 text-right">Marked Paper</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <React.Fragment key={r.id}>
                  <tr className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => setExpanded(expanded === r.id ? null : r.id)} data-testid={`result-row-${r.id}`}>
                    <td className="p-3 text-gray-400">{i + 1}</td>
                    <td className="p-3 font-medium">{r.student_name}</td>
                    <td className="p-3">{r.total} / {r.max_total}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.pct >= 50 ? 'bg-green-100 text-green-700' : r.pct >= 35 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{r.pct}%</span>
                    </td>
                    <td className="p-3 text-xs text-red-600">{r.flags && <span title={r.flags}><AlertTriangle className="w-4 h-4 inline" /></span>}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); downloadPdf(r); }} data-testid={`download-pdf-${r.id}`}>
                        <Download className="w-3.5 h-3.5 mr-1" /> PDF
                      </Button>
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr className="border-t bg-blue-50/40">
                      <td colSpan={6} className="p-4 text-sm">
                        {r.flags && <div className="text-red-600 mb-2 text-xs">⚠ {r.flags}</div>}
                        <div className="font-medium mb-1">AI Feedback</div>
                        <p className="text-gray-700 mb-3">{r.feedback}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(r.per_q || {}).map(([q, e]) => (
                            <span key={q} className={`text-xs px-2 py-0.5 rounded border ${e.awarded === e.max ? 'bg-green-50 border-green-200 text-green-700' : e.awarded === 0 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                              {q}: {e.awarded}/{e.max}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {results.length === 0 && job.status !== 'processing' && (
        <div className="border-2 border-dashed rounded-xl p-12 text-center text-gray-400" data-testid="no-results-placeholder">No results yet.</div>
      )}
    </div>
  );
};

const PublishModal = ({ jobId, jobTitle, onClose }) => {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [term, setTerm] = useState('Term 1');
  const [students, setStudents] = useState([]);
  const [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`/api/exam-marking/jobs/${jobId}/publish-preview`, { headers: authHeaders() })
      .then((r) => r.json()).then((d) => setClasses(d.classes || []));
  }, [jobId]);

  const loadMatches = async (cid) => {
    setClassId(cid);
    if (!cid) { setMatches([]); return; }
    setBusy(true);
    const res = await fetch(`/api/exam-marking/jobs/${jobId}/publish-preview?class_id=${cid}`, { headers: authHeaders() });
    const d = await res.json();
    setStudents(d.students || []);
    setMatches((d.matches || []).map((m) => ({ ...m, student_id: m.matched_student_id || '' })));
    setBusy(false);
  };

  const publish = async () => {
    const mappings = matches.filter((m) => m.student_id).map((m) => ({ result_id: m.id, student_id: m.student_id }));
    if (!mappings.length) { toast.error('No students matched'); return; }
    setBusy(true);
    try {
      const res = await fetch(`/api/exam-marking/jobs/${jobId}/publish`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ class_id: classId, term, mappings }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail);
      toast.success(`Published ${d.published} exam marks to Gradebook (${d.assessment_title})`);
      onClose();
    } catch (e) { toast.error(e.message || 'Publish failed'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="publish-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6">
        <h3 className="text-lg font-bold mb-1">Publish to Gradebook</h3>
        <p className="text-sm text-gray-500 mb-4">"{jobTitle}" will be saved as an <b>Exam</b> assessment (counts as the 60% exam component)</p>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-sm font-medium">Class</label>
            <select value={classId} onChange={(e) => loadMatches(e.target.value)} className="w-full border rounded-md h-10 px-2 text-sm" data-testid="publish-class-select">
              <option value="">Select class…</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Term</label>
            <select value={term} onChange={(e) => setTerm(e.target.value)} className="w-full border rounded-md h-10 px-2 text-sm" data-testid="publish-term-select">
              {['Term 1', 'Term 2', 'Term 3'].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
        {busy && !matches.length && <div className="text-center p-6"><Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" /></div>}
        {matches.length > 0 && (
          <div className="border rounded-lg overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr><th className="p-2">Paper (folder name)</th><th className="p-2">Score</th><th className="p-2">Gradebook Student</th></tr>
              </thead>
              <tbody>
                {matches.map((m, i) => (
                  <tr key={m.id} className="border-t">
                    <td className="p-2">{m.student_name}{!m.matched_student_id && <span className="text-red-500 text-xs ml-1">(no auto-match)</span>}</td>
                    <td className="p-2">{m.total}/{m.max_total}</td>
                    <td className="p-2">
                      <select value={m.student_id} onChange={(e) => setMatches((ms) => ms.map((x, xi) => xi === i ? { ...x, student_id: e.target.value } : x))}
                        className={`w-full border rounded-md h-9 px-2 text-sm ${!m.student_id ? 'border-red-300 bg-red-50' : ''}`} data-testid={`match-select-${i}`}>
                        <option value="">— Skip this paper —</option>
                        {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="publish-cancel-btn">Cancel</Button>
          <Button onClick={publish} disabled={busy || !classId} className="bg-indigo-600 hover:bg-indigo-700" data-testid="publish-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Publish {matches.filter((m) => m.student_id).length} Marks
          </Button>
        </div>
      </div>
    </div>
  );
};
